import type {
  ChannelThreadId,
  MessageId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import type { MessageChannel } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import { memo, useCallback } from "react";
import { useMacroState } from "../hooks/use-macro-state";
import { useMessageInputState } from "../hooks/use-message-input-state";
import type { ReplyMode } from "../hooks/use-reply-mode";
import { useReplyMode } from "../hooks/use-reply-mode";
import { MacroSelectorModal } from "./macro-selector-modal";
import { ChatToolbar } from "./message-editor/chat-toolbar";
import { EmailToolbar } from "./message-editor/email-toolbar";
import { MessageEditor } from "./message-editor/message-editor";
import { htmlToPlateContent } from "./message-editor/use-message-editor";
import { MessageInputActions } from "./message-input-actions";
import { AiCopilotDraftNotice } from "./message-input/ai-copilot-draft-notice";
import { BlockedNotice } from "./message-input/blocked-notice";
import { useMessageBlock } from "./message-input/use-message-block";
import { ReplyModeDropdown } from "./reply-mode-dropdown";
export interface EmailRecipients {
  to: { email: string; name: string | null }[];
  cc: { email: string; name: string | null }[];
  bcc: { email: string; name: string | null }[];
}

interface EmailMessageInputProps {
  mode: "email";
  channel: MessageChannel;
  supportTicketId: SupportTicketId;
  threadId: ChannelThreadId;
  /** Optional when composing the first message in a new thread */
  inReplyToMessageId?: MessageId;
  replyRecipients: EmailRecipients;
  initialReplyMode?: ReplyMode;
  onDiscardDraft?: () => void;
}

interface ChatMessageInputProps {
  mode: "chat";
  channel: MessageChannel;
  supportTicketId: SupportTicketId;
  threadId: ChannelThreadId;
  inReplyToMessageId?: MessageId;
}

type MessageInputProps = EmailMessageInputProps | ChatMessageInputProps;

function getPlaceholder(mode: MessageInputProps["mode"], replyMode: ReplyMode) {
  if (replyMode === "internalNote") {
    return "Add an internal note...";
  } else if (mode === "email") {
    return "Type your message...";
  } else {
    return "Start typing...";
  }
}

export const MessageInput = memo(function MessageInput(
  props: MessageInputProps,
) {
  const { mode, supportTicketId, threadId } = props;

  const replyModeState = useReplyMode(
    mode === "email" ? (props.initialReplyMode ?? "reply") : "reply",
  );

  // Block rules apply to public messages (the current channel). Internal
  // notes never leave Redo so they are never blocked.
  const block = useMessageBlock({
    channel: props.channel,
    supportTicketId,
    threadId,
  });
  const isInternalNote = replyModeState.replyMode === "internalNote";
  const effectiveBlock = isInternalNote ? null : block;
  const isBlocked = effectiveBlock !== null;

  const state = useMessageInputState({
    supportTicketId,
    threadId,
    editorMode: mode,
    channel: props.channel,
    inReplyToMessageId: props.inReplyToMessageId,
    replyRecipients: mode === "email" ? props.replyRecipients : undefined,
    onDiscardDraft: mode === "email" ? props.onDiscardDraft : undefined,
    replyMode: replyModeState.replyMode,
    sendDisabled: isBlocked,
  });

  const handleApplyContent = useCallback(
    (html: string) => {
      state.editorState.setValue(htmlToPlateContent(html));
    },
    [state.editorState],
  );

  const macroState = useMacroState(supportTicketId, handleApplyContent);

  const actions = (
    <MessageInputActions
      deleteDraftAction={state.deleteDraftAction}
      isDeletingDraft={state.isDeletingDraft}
      isSavingDraft={state.isSavingDraft}
      isSending={state.isSending}
      onDeleteDraft={state.handleDeleteDraft}
      onSend={state.handleSend}
      onSendAndClose={state.handleSendAndClose}
      onSendAndMarkInProgress={state.handleSendAndMarkInProgress}
      sendAction={state.sendAction}
      sendDisabled={isBlocked}
    />
  );

  const toolbar =
    mode === "email" ? (
      <EmailToolbar
        isGeneratingAiResponse={state.isGeneratingAiResponse}
        onFileSelect={state.handleFileSelect}
        onGenerateAiResponse={state.handleGenerateAiResponse}
        onImageSelect={state.handleImageSelect}
        onMacroClick={macroState.handleMacroClick}
      />
    ) : (
      <ChatToolbar
        onFileSelect={state.handleFileSelect}
        onImageSelect={state.handleImageSelect}
        onMacroClick={macroState.handleMacroClick}
      />
    );

  const aiCopilotDraftNotice =
    mode === "email" &&
    (state.aiSuggestedReply || state.isGeneratingAiResponse) ? (
      <AiCopilotDraftNotice
        loading={state.isGeneratingAiResponse}
        onAccept={state.handleAcceptAiResponse}
        onDismiss={state.handleDismissAiResponse}
        onRefresh={state.handleGenerateAiResponse}
        suggestedReply={state.aiSuggestedReply}
      />
    ) : null;

  return (
    <>
      <div
        className={`flex w-full flex-col${mode === "email" ? " pt-4" : ""}`}
        ref={state.containerRef}
      >
        <MessageEditor
          actions={actions}
          attachments={state.attachments}
          banner={
            effectiveBlock ? (
              <BlockedNotice block={effectiveBlock} />
            ) : (
              aiCopilotDraftNotice
            )
          }
          className="w-full"
          disabled={isBlocked}
          editorState={state.editorState}
          header={
            mode === "email" ? (
              <ReplyModeDropdown replyModeState={replyModeState} />
            ) : undefined
          }
          onChange={state.handleEditorChange}
          onRemoveAttachment={state.handleRemoveAttachment}
          placeholder={getPlaceholder(mode, replyModeState.replyMode)}
          replyMode={replyModeState.replyMode}
          toolbar={toolbar}
        />
      </div>
      <MacroSelectorModal
        isApplying={macroState.isApplyingMacro}
        macros={macroState.macros}
        onApplyMacro={macroState.handleApplyMacro}
        onClose={macroState.handleCloseMacroModal}
        open={macroState.macroModalOpen}
      />
    </>
  );
});
